import React, { useEffect, useState, useRef } from "react";
import ReactDOM from 'react-dom';
import * as Modal from 'react-modal';
import { useSelector, useDispatch } from "react-redux";
import {
  setNotesPanelExpaned,
  setRollDownExpanded,
  removeNote,
  setRelatedFilter,
  setFilterPanelOpen,
} from "../redux/modules/document";
import { IStateTree } from "../redux/store";
import NoteSidebar from "./noteSidebar";
import NoteInlineEditor from "./noteInlineEditor";
import RelatedMaterialFilter from "./relatedMaterialFilter";
import { useFocusTrap } from "./useFocusTrap";
import { NoteService } from "../services/noteService";
import { SelectorService } from "../services/SelectorService";
import { Toaster } from "../modules/ui/toaster";
import { INoteCategory } from "../dto/INoteCategory";
import { INoteData } from "../dto/INoteData";
import CourseCard from "./courseCard";
import { noteMatchesFilter, courseMatchesFilter, countActiveFilters } from "../modules/helpers/relatedFilter";

interface INoteShowOrHideProps {
    disableTools: boolean;
}

const MEDIUM_DESKTOP = 1460;
const DESKTOP = 1200;

// Anteckningskort i mobil-modalen: samma NoteInlineEditor som högerpanelen
// (penn-ikon → inline-redigering i kortet). Radera-flödet speglar note.tsx:
// bekräftelsedialog → deleteNoteById → removeNote + markering städas.
interface IModalNoteCardProps {
    note: INoteData;
    highlighted: boolean;
    categories: INoteCategory[];
}

function ModalNoteCard({ note, highlighted, categories }: IModalNoteCardProps) {
    const dispatch = useDispatch();
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const onConfirmDelete = () => {
        const service = new NoteService();
        service.deleteNoteById(note.id,
            () => {
                dispatch(removeNote(note));
                Toaster.show("Anteckning raderad", true);
                setIsDeleteDialogOpen(false);
                document.body.classList.remove('note-editor-open');
                if (note.selection != null) {
                    SelectorService.instance.remove(note.elementId);
                }
            },
            () => {
                alert('Ett fel har uppstått. Anteckningen kunde inte tas bort');
            });
    };

    Modal.setAppElement('#document-main');

    return (
        <div id={'modal-note-' + note.id} className={`modal-note-wrapper ${highlighted ? 'modal-note-wrapper--highlighted' : ''}`}>
            <NoteInlineEditor
                note={note}
                categories={categories}
                isBundled={false}
                overlapsSibling={false}
                onDelete={() => setIsDeleteDialogOpen(true)}
            />
            <Modal isOpen={isDeleteDialogOpen} onRequestClose={() => setIsDeleteDialogOpen(false)} overlayClassName="modal__overlay" className="modal__window" contentLabel="Ta bort anteckning">
                <button onClick={() => setIsDeleteDialogOpen(false)} className="modal__close">Stäng</button>

                <div className="modal__content">
                    <h3 className="modal__title">Vill du radera din anteckning?</h3>
                    <p>Din anteckning kommer raderas och du kan inte återskapa den.</p>

                    <div className="modalCTAs">
                        <button onClick={() => setIsDeleteDialogOpen(false)} className="button__primary">Radera inte</button>
                        <button onClick={onConfirmDelete} className="button__secondary">Radera</button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default function NoteShowOrHide(props: INoteShowOrHideProps) {
  const dispatch = useDispatch();
  const panelRef = useRef<HTMLDivElement | null>(null);

  const rollDownState = useSelector(
    (state: IStateTree) => state.document.rollDownState
  );
  const expanedState = useSelector(
    (state: IStateTree) => state.document.notesPanelExpaned
  );
  const showNotes = useSelector(
    (state: IStateTree) => state.document.showNotes
  );
  const notes = useSelector(
    (state: IStateTree) => state.document.notes
  );
  const currentNote = useSelector(
    (state: IStateTree) => state.document.currentNote
  );
  const editingNoteId = useSelector(
    (state: IStateTree) => state.document.editingNoteId
  );
  const noteWasUpdated = useSelector(
    (state: IStateTree) => state.document.aNoteWasUpdated
  );
  const courses = useSelector(
    (state: IStateTree) => state.document.courses
  );
  const highlightedCourseIndex = useSelector(
    (state: IStateTree) => state.document.highlightedCourseIndex
  );
  const relatedFilter = useSelector(
    (state: IStateTree) => state.document.relatedFilter
  );
  const filterPanelOpen = useSelector(
    (state: IStateTree) => state.document.filterPanelOpen
  );

  const [availableCategories, setAvailableCategories] = useState<INoteCategory[]>([]);
  const [isMobileWidth, setIsMobileWidth] = useState<boolean>(window.innerWidth < DESKTOP);

  // Enbart kort som klarar filtret ska renderas. highlightedCourseIndex (satt av
  // noteSidebar.tsx) refererar index inom DENNA filtrerade lista.
  const visibleCourses = (courses || []).filter(c => courseMatchesFilter(c, relatedFilter));

  useFocusTrap(isMobileWidth && expanedState, panelRef);

  // Håll koll på mobil-läget (<1200px) – där visas panelen som modal med kortlista
  useEffect(() => {
    const handleResize = () => setIsMobileWidth(window.innerWidth < DESKTOP);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // När modalen öppnas med en vald anteckning (via ikon- eller markeringsklick):
  // scrolla modalens lista till kortet. Highlight sker via currentNote i render.
  useEffect(() => {
    if (!isMobileWidth || !expanedState || !currentNote) return;
    const timer = setTimeout(() => {
      document.getElementById('modal-note-' + currentNote.id)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    return () => clearTimeout(timer);
  }, [expanedState, currentNote, isMobileWidth]);

  // Tvinga panelen expanderad vid >=1460px (medium-desktop)
  useEffect(() => {
    const forceExpandIfLargeScreen = () => {
      if (window.innerWidth >= MEDIUM_DESKTOP) {
        dispatch(setNotesPanelExpaned(true));
        dispatch(setRollDownExpanded(false));
      }
    };
    forceExpandIfLargeScreen();
    window.addEventListener("resize", forceExpandIfLargeScreen);
    return () => window.removeEventListener("resize", forceExpandIfLargeScreen);
  }, []);

  // Scroll-lock bara vid <1460px
  useEffect(() => {
    if (expanedState && window.innerWidth < MEDIUM_DESKTOP) {
      document.documentElement.classList.add("scroll-lock");
    } else {
      document.documentElement.classList.remove("scroll-lock");
    }
    return () => {
      document.documentElement.classList.remove("scroll-lock");
    };
  }, [expanedState]);

  // Mode B: body-klass för A/B-layout vid 1200–1460px
  useEffect(() => {
    const updateModeClass = () => {
      const isDesktopRange = window.innerWidth >= DESKTOP && window.innerWidth < MEDIUM_DESKTOP;
      document.body.classList.toggle("document-mode-b", isDesktopRange && expanedState);
    };
    updateModeClass();
    window.addEventListener("resize", updateModeClass);
    return () => {
      window.removeEventListener("resize", updateModeClass);
      document.body.classList.remove("document-mode-b");
    };
  }, [expanedState]);

  // Lyssna på CustomEvent från "Innehåll"-knappen
  useEffect(() => {
    const handleCollapsePanel = () => {
      dispatch(setNotesPanelExpaned(false));
      dispatch(setRollDownExpanded(false));
    };
    window.addEventListener("collapseRightPanel", handleCollapsePanel);
    return () => window.removeEventListener("collapseRightPanel", handleCollapsePanel);
  }, [dispatch]);

  // Escape stänger panelen (enbart vid <1460px)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && window.innerWidth < MEDIUM_DESKTOP) {
        dispatch(setNotesPanelExpaned(false));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [dispatch]);

  // Stäng filterpanelen vid klick utanför den. .panel-filtrera-wrapper är den
  // gemensamma ancestorn för både knappen (desktop/mobil) och dropdownen.
  useEffect(() => {
    if (!filterPanelOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.panel-filtrera-wrapper')) return;
      dispatch(setFilterPanelOpen(false));
    };
    document.addEventListener('click', handleOutsideClick, true);
    return () => document.removeEventListener('click', handleOutsideClick, true);
  }, [filterPanelOpen, dispatch]);

  useEffect(() => {
    getCategoriesForThisDocument();
  }, [noteWasUpdated]);

  useEffect(() => {
    const docContent = document.getElementById('documentcontent');
    if (docContent) {
      if (showNotes) {
        docContent.classList.remove('xml_content--hide-markings');
      } else {
        docContent.classList.add('xml_content--hide-markings');
      }
    }
  }, [showNotes]);

  useEffect(() => {
    updateStylingForMarkerBelongingToCurrentNote();
  }, [currentNote]);

  const updateStylingForMarkerBelongingToCurrentNote = () => {
    document.querySelectorAll('.marking--selected').forEach((element) => {
      element.classList.remove('marking--selected');
    });
    if (currentNote) {
      const currentMarkerElement = document.getElementById('marking-' + currentNote.id);
      if (currentMarkerElement) {
        const dataSelectionId = $("#marking-" + currentNote.id).attr("data-selection-id");
        if (dataSelectionId) {
          $("[data-selection-id='" + dataSelectionId + "']").addClass('marking--selected');
        }
      }
    }
  };

  const getCategoriesForThisDocument = () => {
    const service = new NoteService();
    service.getNoteCategories((data: INoteCategory[]) => {
      const cats: INoteCategory[] = [];
      data.forEach(cat => {
        const categoryNotes: any[] = [];
        notes.forEach(aNote => {
          if (aNote.categoryId === cat.categoryId) {
            categoryNotes.push(aNote);
          }
        });
        cat.notes = categoryNotes;
        cats.push(cat);
      });
      setAvailableCategories(cats);
    }, (err) => {
      console.log('could not get categories', err);
    });
  };

  const toggleNoteListVisibilityReduxState = () => {
    if (window.innerWidth >= MEDIUM_DESKTOP) {
      return;
    }
    window.dispatchEvent(new Event("syncHeader", { bubbles: false, cancelable: false }));
    dispatch(setNotesPanelExpaned(!expanedState));
    if (window.innerWidth > DESKTOP) {
      dispatch(setRollDownExpanded(false));
    } else {
      dispatch(setRollDownExpanded(!expanedState));
    }
    $(".note__content.note__content--active").removeClass('note__content--active');
    document.body.classList.remove('note-editor-open');
  };

  const handleOpenCourses = () => {
    dispatch(setNotesPanelExpaned(true));
  };

  // Anteckningarna i dokumentordning (ankarens position), oberoende av kortens
  // faktiska placering – i modalen staplas de direkt efter varandra. Enbart kort
  // som klarar filtret ska renderas.
  const getNotesInDocumentOrder = (): INoteData[] => {
    return notes
      .filter(note => noteMatchesFilter(note, relatedFilter, editingNoteId))
      .sort((a, b) => {
        const aTop = a.elementId ? ($(a.elementId).offset()?.top ?? 0) : 0;
        const bTop = b.elementId ? ($(b.elementId).offset()?.top ?? 0) : 0;
        return aTop - bTop;
      });
  };


  const openLogin = (e: React.MouseEvent) => {
    e.stopPropagation();
    ($("#loginButton")[0] as HTMLElement).click();
  };

  const notesPortalContainer = document.getElementById('documentnotes');
  const coursesPortalContainer = document.getElementById('documentsidebar-content');

  let hideTools = false;
  const containerNoteHideTools = document.getElementById('notes-hide-tools');
  if (containerNoteHideTools) {
    hideTools = containerNoteHideTools.getAttribute('data-note-hide-tools') == 'true';
  }

  return (
    <>
     <div className="noteOverlay"></div>
      <div
        className={`notes-show-or-hide notesWrapper ${expanedState ? 'expanded' : ''} ${expanedState && (props.disableTools || hideTools) ? "notes-show-or-hide--tools-disabled" : ""} ${hideTools ? 'tooltip__action-disabled' : ''}`}
        style={expanedState ? { width: "394px" } : {}}
      >
        {expanedState && props.disableTools && !hideTools && (
          <button className="showLoginBtn button__secondary" onClick={openLogin}>
            Logga in med personkonto
          </button>
        )}

        <div className={expanedState ? "showPanel" : ""} id="rightcolumnpanel" ref={panelRef}>
          {/* Mobil-modal (<1200px, expanderad): egen header med stäng-kryss,
              titel och Filtrera-länk (Figma node 19509:30443). */}
          {isMobileWidth && expanedState ? (
            <div className="modal-header">
              <button
                className="modal-header__close"
                aria-label="Stäng relaterat material"
                onClick={toggleNoteListVisibilityReduxState}
              >
                <span className="modal-header__close-icon" aria-hidden="true"></span>
              </button>
              <strong className="modal-header__title">Relaterat material</strong>
              {/* Wrapper delar border med dropdownen när panelen är öppen, så Filtrera-
                  knappen visuellt ramas in tillsammans med panelen (Figma node 19668:45772).
                  Dropdownen nestlad här (inte längre ner bland korten) så den hamnar direkt
                  under knappen istället för att flyta till botten av modalen. */}
              <div className={`panel-filtrera-wrapper ${filterPanelOpen ? 'panel-filtrera-wrapper--open' : ''}`}>
                <button
                  className="modal-header__filter"
                  aria-label="Filtrera anteckningar"
                  aria-expanded={filterPanelOpen}
                  onClick={(e) => { e.stopPropagation(); dispatch(setFilterPanelOpen(!filterPanelOpen)); }}
                >
                  Filtrera
                  {countActiveFilters(relatedFilter) > 0 && (
                    <span className="panel-filtrera__badge">{countActiveFilters(relatedFilter)}</span>
                  )}
                  <span className="panel-filtrera__arrow" aria-hidden="true"></span>
                </button>
                {filterPanelOpen && (
                  <RelatedMaterialFilter
                    className="related-filter--dropdown"
                    notesCount={notes.length}
                    coursesCount={courses?.length ?? 0}
                    filter={relatedFilter}
                    onChange={(filter) => dispatch(setRelatedFilter(filter))}
                    onClose={() => dispatch(setFilterPanelOpen(false))}
                  />
                )}
              </div>
            </div>
          ) : (
            /* Toggle-knapp – döljs via CSS vid >=1460px */
            <div className="headerright-text">
                <div
                  aria-label={expanedState ? 'Stäng relaterat material' : 'Öppna relaterat material'}
                  className={`toggleSection ${expanedState ? 'expanded' : ''} ${rollDownState ? 'rolldownActive' : ''}`}
                  data-modalid="rightcolumnpanel"
                  role="button"
                  tabIndex={isMobileWidth ? 0 : -1}
                  onClick={toggleNoteListVisibilityReduxState}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleNoteListVisibilityReduxState();
                    }
                  }}
                >
                  {!expanedState && <div className="leftArrow" />}
                  {expanedState && <div className="rightArrow" />}
                  <div className="headerSection">
                    <strong className="notesTitle">Relaterat material</strong>
                  </div>
                </div>
            </div>
          )}
          <p className="panel-description">Här visas relaterade kurser och personliga anteckningar.</p>

          {/* Mobil-modal (<1200px): alla kort staplade direkt efter varandra i
              dokumentordning – kurskort först, därefter anteckningskorten. Enbart
              kort som klarar filtret renderas. */}
          {isMobileWidth && expanedState && (
            <div className="modal-cards-list">
              {visibleCourses.map((course, i) => (
                <CourseCard
                  key={'course-' + i}
                  course={course}
                  highlighted={highlightedCourseIndex === i}
                />
              ))}
              {showNotes && getNotesInDocumentOrder().map((note) => (
                <ModalNoteCard
                  key={note.id}
                  note={note}
                  highlighted={!!currentNote && currentNote.id === note.id}
                  categories={availableCategories}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {/* En portal för båda så kurskorten alltid hamnar först i DOM/tabb-ordning */}
      {notesPortalContainer && ReactDOM.createPortal(
        <>
          {visibleCourses.length > 0 && expanedState && !isMobileWidth && (
            <div className="course-cards-sidebar">
              {visibleCourses.map((course, i) => (
                <CourseCard
                  key={i}
                  course={course}
                  highlighted={highlightedCourseIndex === i}
                />
              ))}
            </div>
          )}
          {showNotes && <NoteSidebar />}
        </>,
        notesPortalContainer
      )}

      {/* Kurs-ikon i sidofältet när panelen är ihopfälld */}
      {visibleCourses.length > 0 && !expanedState && coursesPortalContainer && ReactDOM.createPortal(
        <button
          className="course-collapsed-icon"
          aria-label={`${visibleCourses.length} relaterade kurser`}
          onClick={handleOpenCourses}
        >
          <span className="course-collapsed-icon__symbol" aria-hidden="true"></span>
          <span className="course-collapsed-icon__badge">{visibleCourses.length}</span>
        </button>,
        coursesPortalContainer
      )}

      {/* Knapp som öppnar modalen på smala skärmar (<960px) – ersätter .toggleSection
          där (CSS döljer .toggleSection:not(.expanded) under 960px). Figma: "Mina
          noteringar Slide in closed" (node 19494:22631). */}
      {!expanedState && coursesPortalContainer && ReactDOM.createPortal(
        <button
          className="mobile-notes-trigger"
          aria-label="Öppna relaterat material"
          onClick={toggleNoteListVisibilityReduxState}
        >
          <span className="mobile-notes-trigger__icon" aria-hidden="true"></span>
        </button>,
        coursesPortalContainer
      )}

      </>
  );
}
